import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { chargerService } from '../services/chargerService';
import { Session, QueueEntry, Charger } from '../types';
import { 
  Zap, 
  Clock, 
  X, 
  Calendar,
  Users,
  Play,
  AlertCircle,
  CheckCircle
} from 'lucide-react';

export function MySessionPage() {
  const { t } = useTranslation();
  const [currentSession, setCurrentSession] = useState<Session | null>(null);
  const [sessionCharger, setSessionCharger] = useState<Charger | null>(null);
  const [queueEntries, setQueueEntries] = useState<QueueEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isActioning, setIsActioning] = useState(false);
  const [selectedAction, setSelectedAction] = useState<'extend' | 'reduce' | 'stop' | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [extensionMinutes, setExtensionMinutes] = useState(20);
  const [reductionMinutes, setReductionMinutes] = useState(10);
  const [customMinutes, setCustomMinutes] = useState('');
  const [useCustom, setUseCustom] = useState(false);

  useEffect(() => {
    loadSessionData();
  }, []);

  const loadSessionData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Load current session and queue entries in parallel
      const [sessionData, queueData] = await Promise.all([
        chargerService.getCurrentSession().catch((err) => {
          console.error('❌ Failed to fetch current session. This is OK if you don\'t have an active session:', err);
          return null;
        }),
        chargerService.getMyQueueEntries().catch((err) => {
          console.error('❌ CRITICAL: Failed to fetch queue entries:', err);
          return [];
        })
      ]);
      
      console.log('📊 Session Data:', sessionData);
      console.log('📊 Queue Data (Queue Entries):', queueData);
      console.log(`✅ Total queue entries found: ${queueData.length}`);
      
      setCurrentSession(sessionData);
      setQueueEntries(queueData);

      // If we have a session, load the charger details
      if (sessionData) {
        try {
          const chargerData = await chargerService.getCharger(sessionData.chargerId);
          setSessionCharger(chargerData);
        } catch (err) {
          console.error('Failed to load charger details:', err);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load session data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStopSession = async () => {
    if (!currentSession) return;
    
    try {
      setIsActioning(true);
      await chargerService.stopCharging(currentSession.chargerId);
      setSelectedAction(null);
      await loadSessionData(); // Refresh data
    } catch (error) {
      console.error('Failed to stop session:', error);
    } finally {
      setIsActioning(false);
    }
  };

  const handleExtendSession = async (additionalMinutes: number) => {
    if (!currentSession) return;
    
    try {
      setIsActioning(true);
      await chargerService.extendCharging(currentSession.chargerId, { additionalMinutes });
      setSelectedAction(null);
      await loadSessionData(); // Refresh data
    } catch (error) {
      console.error('Failed to extend session:', error);
    } finally {
      setIsActioning(false);
    }
  };

  const handleReduceSession = async (minutesToReduce: number) => {
    if (!currentSession) return;
    
    try {
      setIsActioning(true);
      setActionError(null); // Clear any previous errors
      await chargerService.reduceCharging(currentSession.chargerId, { minutesToReduce });
      setSelectedAction(null);
      await loadSessionData(); // Refresh data
    } catch (error: any) {
      console.error('Failed to reduce session:', error);
      console.error('Error response:', error.response?.data);
      
      let errorMessage = t('errors.generic');
      
      // Check for localization keys from backend validation
      if (error.response?.data?.message) {
        const serverMessage = error.response.data.message;
        console.log('Server message:', serverMessage);
        if (serverMessage === 'reductionTimeExceedsMaximum') {
          errorMessage = t('errors.reductionTimeExceedsMaximum');
        } else if (serverMessage === 'reductionBelowMinimumBuffer') {
          errorMessage = t('errors.reductionBelowMinimumBuffer');
        } else if (serverMessage === 'reductionBelowMinimumDuration') {
          errorMessage = t('errors.reductionBelowMinimumDuration');
        } else if (serverMessage === 'invalidReductionDuration') {
          errorMessage = t('errors.invalidReductionDuration');
        } else if (serverMessage === 'invalidChargingDuration') {
          errorMessage = t('errors.invalidChargingDuration');
        } else {
          errorMessage = serverMessage;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setActionError(errorMessage);
    } finally {
      setIsActioning(false);
    }
  };

  const handleCancelQueueEntry = async (chargerId: string) => {
    try {
      setIsActioning(true);
      console.log(`🚪 Attempting to leave queue for charger ${chargerId}...`);
      await chargerService.leaveQueue(chargerId);
      console.log(`✅ Successfully left queue for charger ${chargerId}`);
      await loadSessionData(); // Refresh data
    } catch (error) {
      console.error(`❌ Failed to leave queue for charger ${chargerId}:`, error);
    } finally {
      setIsActioning(false);
    }
  };

  const handleAcceptQueue = async (chargerId: string) => {
    try {
      setIsActioning(true);
      await chargerService.acceptQueue(chargerId);
      await loadSessionData(); // Refresh data
    } catch (error) {
      console.error('Failed to accept queue position:', error);
    } finally {
      setIsActioning(false);
    }
  };

  const handlePostponeQueue = async (chargerId: string) => {
    try {
      setIsActioning(true);
      await chargerService.postponeQueue(chargerId);
      await loadSessionData(); // Refresh data
    } catch (error) {
      console.error('Failed to postpone queue position:', error);
    } finally {
      setIsActioning(false);
    }
  };

  const handleAction = () => {
    setActionError(null); // Clear any previous errors
    if (selectedAction === 'stop') {
      handleStopSession();
    } else if (selectedAction === 'extend') {
      const minutes = useCustom ? parseInt(customMinutes) || 20 : extensionMinutes;
      handleExtendSession(minutes);
    } else if (selectedAction === 'reduce') {
      const minutes = useCustom ? parseInt(customMinutes) || 10 : reductionMinutes;
      handleReduceSession(minutes);
    }
  };

  const formatEndTime = (timeStr?: string) => {
    if (!timeStr) return t('common.unknown');
    return new Date(timeStr).toLocaleTimeString();
  };

  // Calculate time remaining percentage for progress bar
  const getSessionProgress = () => {
    if (!currentSession) return 0;
    const now = new Date().getTime();
    const start = new Date(currentSession.startedAt).getTime();
    const end = new Date(currentSession.estimatedEndAt).getTime();
    const total = end - start;
    const elapsed = now - start;
    const percentage = Math.min(Math.max((elapsed / total) * 100, 0), 100);
    return percentage;
  };

  // Calculate remaining minutes
  const getTimeRemaining = () => {
    if (!currentSession) return 0;
    const now = new Date().getTime();
    const end = new Date(currentSession.estimatedEndAt).getTime();
    const remaining = Math.max(0, Math.floor((end - now) / 1000 / 60));
    return remaining;
  };

  // Get queue status text
  const getQueueStatus = () => {
    const readyQueues = queueEntries.filter(e => e.canAccept).length;
    const waitingQueues = queueEntries.length - readyQueues;
    
    if (queueEntries.length === 0) return 'No pending positions';
    if (readyQueues > 0) return `${readyQueues} position(s) ready for you!`;
    return `${waitingQueues} position(s) waiting`;
  };

  const extensionPresets = [
    { minutes: 10, label: t('dialogs.updateCharging.presets.10'), subtitle: t('dialogs.updateCharging.subtitles.quick') },
    { minutes: 20, label: t('dialogs.updateCharging.presets.20'), subtitle: t('dialogs.updateCharging.subtitles.standard') },
    { minutes: 30, label: t('dialogs.updateCharging.presets.30'), subtitle: t('dialogs.updateCharging.subtitles.extended') },
    { minutes: 40, label: t('dialogs.updateCharging.presets.40'), subtitle: t('dialogs.updateCharging.subtitles.long') }
  ];

  const reductionPresets = [
    { minutes: 10, label: t('dialogs.updateCharging.presets.10'), subtitle: t('dialogs.updateCharging.subtitles.quickReduce') },
    { minutes: 20, label: t('dialogs.updateCharging.presets.20'), subtitle: t('dialogs.updateCharging.subtitles.standardReduce') },
    { minutes: 30, label: t('dialogs.updateCharging.presets.30'), subtitle: t('dialogs.updateCharging.subtitles.extendedReduce') },
    { minutes: 40, label: t('dialogs.updateCharging.presets.40'), subtitle: t('dialogs.updateCharging.subtitles.longReduce') }
  ];

  if (isLoading) {
    return (
      <div className="modern-loading">
        <Clock className="animate-spin loading-spinner" size={32} />
        <p className="loading-text">{t('common.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modern-error">
        <div className="error-card">
          <div className="error-icon">
            <AlertCircle size={48} />
          </div>
          <h2 className="error-title">{t('common.error')}</h2>
          <p className="error-message">{error}</p>
          <button className="btn-modern btn-primary-modern" onClick={loadSessionData}>
            {t('common.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  // If no session and no queue entries, show empty state
  if (!currentSession && queueEntries.length === 0) {
    return (
      <div className="modern-page">
        <div className="modern-empty-state">
          <div className="empty-icon">
            <Calendar size={64} />
          </div>
          <h2 className="empty-title">{t('session.noActiveSession')}</h2>
          <p className="empty-description">
            {t('session.startNewSession')}
          </p>
          <button className="btn-modern btn-primary-modern" onClick={() => window.history.back()}>
            <Zap size={16} />
            {t('home.title')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="modern-page">
      {/* Active Session Section with enhanced display */}
      {currentSession && (
        <div className="session-modal-container">
          <div className="modal-modern session-page-modal">
            {/* Session Header */}
            <div className="modal-header-modern">
              <div className="session-header-content">
                <h3 className="modal-title">
                  <Zap size={20} style={{ color: currentSession.isOverdue ? 'var(--color-error)' : 'var(--color-charging)' }} />
                  {currentSession.isOverdue ? 'Session Overdue' : t('dialogs.updateCharging.currentSession')}
                </h3>
                <p className="modal-subtitle">
                  {sessionCharger ? `${sessionCharger.name} - ${t('home.charger.slot', { number: sessionCharger.slotNumber })}` : t('common.loading')}
                </p>
              </div>
              {/* Session Status Badge */}
              <div className={`session-status-badge ${currentSession.isOverdue ? 'overdue' : 'active'}`}>
                {currentSession.isOverdue ? '⚠️ Overdue' : '⚡ Active'}
              </div>
            </div>
            
            <div className="modal-content-modern">
              {/* Session Progress Section */}
              <div className="session-progress-section">
                <div className="progress-header">
                  <span className="progress-label">Session Duration</span>
                  <span className="time-remaining">
                    {getTimeRemaining()} min remaining
                  </span>
                </div>
                <div className="progress-bar-container">
                  <div 
                    className={`progress-bar ${currentSession.isOverdue ? 'overdue' : 'active'}`}
                    style={{ width: `${getSessionProgress()}%` }}
                  />
                </div>
              </div>

              {/* Session Details Card */}
              <div className="current-session-info">
                {currentSession.isOverdue && (
                  <div className="overdue-warning-banner">
                    <div className="overdue-icon">⚠️</div>
                    <div className="overdue-text">
                      <strong>Session Overdue</strong>
                      <p>Your session has exceeded the maximum allowed time. Please end your session when you're done charging.</p>
                    </div>
                  </div>
                )}
                <div className="session-details">
                  <div className="session-detail">
                    <Zap size={16} style={{ color: currentSession.isOverdue ? 'var(--color-error)' : 'var(--color-charging)' }} />
                    <span>{currentSession.isOverdue ? 'Session Overdue' : t('dialogs.updateCharging.chargingInProgress')}</span>
                  </div>
                  <div className="session-detail">
                    <Clock size={16} style={{ color: currentSession.isOverdue ? 'var(--color-error)' : 'var(--color-queue)' }} />
                    <span>
                      {currentSession.isOverdue 
                        ? `Max time exceeded: ${formatEndTime(currentSession.maxEndAt)}`
                        : `${t('dialogs.updateCharging.estimatedEnd')}: ${formatEndTime(currentSession.estimatedEndAt)}`
                      }
                    </span>
                  </div>
                </div>
              </div>

              {/* Queue Status Overview - NEW */}
              {queueEntries.length > 0 && (
                <div className="queue-status-overview">
                  <div className="queue-status-header">
                    <Users size={16} />
                    <span>Your Queue Positions</span>
                  </div>
                  <div className="queue-status-content">
                    <div className="queue-stat">
                      <span className="stat-value">{queueEntries.length}</span>
                      <span className="stat-label">Total Positions</span>
                    </div>
                    <div className="queue-stat">
                      <span className="stat-value" style={{ color: queueEntries.some(e => e.canAccept) ? 'var(--color-success)' : 'var(--color-warning)' }}>
                        {queueEntries.filter(e => e.canAccept).length}
                      </span>
                      <span className="stat-label">Ready for You</span>
                    </div>
                    <div className="queue-stat">
                      <span className="stat-value">{queueEntries.filter(e => !e.canAccept).length}</span>
                      <span className="stat-label">Waiting</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="action-selection">
                <h4>{t('dialogs.updateCharging.whatToDo')}</h4>
                
                <div className="action-options">
                  <button
                    className={`action-option ${selectedAction === 'extend' ? 'action-option-active' : ''}`}
                    onClick={() => setSelectedAction('extend')}
                  >
                    <div className="action-icon" style={{ backgroundColor: 'var(--color-primary-bg)' }}>
                      <Clock size={20} style={{ color: 'var(--color-primary)' }} />
                    </div>
                    <div className="action-details">
                      <div className="action-title">{t('dialogs.updateCharging.extendSession')}</div>
                      <div className="action-subtitle">{t('dialogs.updateCharging.addMoreTime')}</div>
                    </div>
                  </button>

                  <button
                    className={`action-option ${selectedAction === 'reduce' ? 'action-option-active' : ''}`}
                    onClick={() => setSelectedAction('reduce')}
                  >
                    <div className="action-icon" style={{ backgroundColor: 'var(--color-warning-bg)' }}>
                      <Clock size={20} style={{ color: 'var(--color-warning)' }} />
                    </div>
                    <div className="action-details">
                      <div className="action-title">{t('dialogs.updateCharging.reduceSession')}</div>
                      <div className="action-subtitle">{t('dialogs.updateCharging.reduceTime')}</div>
                    </div>
                  </button>

                  <button
                    className={`action-option ${selectedAction === 'stop' ? 'action-option-active' : ''}`}
                    onClick={() => setSelectedAction('stop')}
                  >
                    <div className="action-icon" style={{ backgroundColor: 'var(--color-error-bg)' }}>
                      <X size={20} style={{ color: 'var(--color-error)' }} />
                    </div>
                    <div className="action-details">
                      <div className="action-title">{t('dialogs.updateCharging.stopCharging')}</div>
                      <div className="action-subtitle">{t('dialogs.updateCharging.endSession')}</div>
                    </div>
                  </button>
                </div>
              </div>

              {selectedAction === 'extend' && (
                <div className="extension-options">
                  <h4>{t('dialogs.updateCharging.howMuchTime')}</h4>
                  
                  <div className="time-presets-modern">
                    {extensionPresets.map(({ minutes, label, subtitle }) => (
                      <button
                        key={minutes}
                        className={`preset-btn-modern ${!useCustom && extensionMinutes === minutes ? 'preset-btn-active-modern' : ''}`}
                        onClick={() => {
                          setExtensionMinutes(minutes);
                          setUseCustom(false);
                        }}
                      >
                        <div className="preset-label">{label}</div>
                        <div className="preset-subtitle">{subtitle}</div>
                      </button>
                    ))}
                  </div>

                  <div className="custom-time-modern">
                    <button
                      className={`custom-option-modern ${useCustom ? 'custom-option-active-modern' : ''}`}
                      onClick={() => setUseCustom(true)}
                    >
                      <div className="custom-option-content">
                        <span className="custom-option-label">{t('dialogs.updateCharging.customTime')}</span>
                        {useCustom && (
                          <input
                            type="number"
                            value={customMinutes}
                            onChange={(e) => setCustomMinutes(e.target.value)}
                            placeholder={t('dialogs.updateCharging.minutesPlaceholder')}
                            min="1"
                            max="120"
                            className="custom-input-modern"
                            autoFocus
                          />
                        )}
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {selectedAction === 'reduce' && (
                <div className="extension-options">
                  <h4>{t('dialogs.updateCharging.howMuchReduce')}</h4>
                  
                  <div className="time-presets-modern">
                    {reductionPresets.map(({ minutes, label, subtitle }) => (
                      <button
                        key={minutes}
                        className={`preset-btn-modern ${!useCustom && reductionMinutes === minutes ? 'preset-btn-active-modern' : ''}`}
                        onClick={() => {
                          setReductionMinutes(minutes);
                          setUseCustom(false);
                        }}
                      >
                        <div className="preset-label">{label}</div>
                        <div className="preset-subtitle">{subtitle}</div>
                      </button>
                    ))}
                  </div>

                  <div className="custom-time-modern">
                    <button
                      className={`custom-option-modern ${useCustom ? 'custom-option-active-modern' : ''}`}
                      onClick={() => setUseCustom(true)}
                    >
                      <div className="custom-option-content">
                        <span className="custom-option-label">{t('dialogs.updateCharging.customTime')}</span>
                        {useCustom && (
                          <input
                            type="number"
                            value={customMinutes}
                            onChange={(e) => setCustomMinutes(e.target.value)}
                            placeholder={t('dialogs.updateCharging.minutesPlaceholder')}
                            min="1"
                            max="240"
                            className="custom-input-modern"
                            autoFocus
                          />
                        )}
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {selectedAction === 'stop' && (
                <div className="stop-confirmation">
                  <div className="warning-message">
                    <div className="warning-icon">⚠️</div>
                    <div className="warning-text">
                      <strong>{t('dialogs.updateCharging.confirmStop')}</strong>
                      <p>{t('dialogs.updateCharging.stopWarning')}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {actionError && (
              <div className="error-message-modern" style={{ margin: '16px 24px' }}>
                <AlertCircle size={16} />
                <span>{actionError}</span>
              </div>
            )}

            <div className="modal-actions-modern">
              <button
                className="btn-modern btn-secondary-modern"
                onClick={() => setSelectedAction(null)}
                disabled={isActioning}
              >
                {t('common.cancel')}
              </button>
              <button
                className="btn-modern btn-primary-modern"
                onClick={handleAction}
                disabled={isActioning || !selectedAction}
              >
                {isActioning ? t('common.processing') : 
                 selectedAction === 'stop' ? t('dialogs.updateCharging.buttons.stopCharging') : 
                 selectedAction === 'extend' ? t('dialogs.updateCharging.buttons.extendSession') : 
                 selectedAction === 'reduce' ? t('dialogs.updateCharging.buttons.reduceSession') : 
                 t('dialogs.updateCharging.buttons.selectAction')}
              </button>
            </div>

            {actionError && (
              <div className="error-message-modern" style={{ margin: '16px 24px' }}>
                <AlertCircle size={16} />
                <span>{actionError}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Queue Entries - Enhanced with better visual hierarchy */}
      {queueEntries.length > 0 && (
        <div className="modern-section">
          <div className="section-header">
            <div className="section-header-content">
              <h3 className="section-title">
                <Users size={20} />
                {t('session.queue.title')}
                <span className="count-badge">{queueEntries.length}</span>
              </h3>
              <p className="section-subtitle">{getQueueStatus()}</p>
            </div>
          </div>
          
          {/* Queue Status Indicators - NEW */}
          <div className="queue-status-indicators">
            {queueEntries.filter(e => e.canAccept).length > 0 && (
              <div className="status-indicator ready">
                <CheckCircle size={16} />
                <span>{queueEntries.filter(e => e.canAccept).length} position(s) ready!</span>
              </div>
            )}
            {queueEntries.filter(e => !e.canAccept).length > 0 && (
              <div className="status-indicator waiting">
                <Clock size={16} />
                <span>{queueEntries.filter(e => !e.canAccept).length} position(s) waiting</span>
              </div>
            )}
          </div>
          
          {/* Ready Queue Entries First */}
          {queueEntries.filter(e => e.canAccept).length > 0 && (
            <div className="queue-entries-group">
              <h4 className="queue-group-title ready-title">
                <CheckCircle size={16} style={{ color: 'var(--color-success)' }} />
                Your Turn!
              </h4>
              <div className="queue-entries-grid">
                {queueEntries.filter(e => e.canAccept).map((entry) => (
                  <div key={entry.chargerId} className="modern-queue-card ready">
                    <div className="queue-card-header">
                      <div className="queue-icon ready">
                        <CheckCircle size={20} style={{ color: 'var(--color-success)' }} />
                      </div>
                      <div className="queue-basic-info">
                        <h4 className="queue-charger-name">
                          {entry.chargerName} - {t('home.charger.slot', { number: entry.chargerSlotNumber })}
                        </h4>
                        <p className="queue-position ready">
                          ✓ Position #{entry.position} - Ready Now
                        </p>
                      </div>
                      <div className="queue-status-badge ready">
                        <CheckCircle size={16} />
                        {t('session.queue.yourTurn')}
                      </div>
                    </div>

                    <div className="queue-details-grid">
                      <div className="detail-item-modern">
                        <span className="detail-label">Joined</span>
                        <span className="detail-value">{new Date(entry.joinedAt).toLocaleString()}</span>
                      </div>
                      {entry.estimatedStartTime && (
                        <div className="detail-item-modern">
                          <span className="detail-label">Ready Since</span>
                          <span className="detail-value">{new Date(entry.estimatedStartTime).toLocaleTimeString()}</span>
                        </div>
                      )}
                    </div>

                    <div className="queue-actions-modern">
                      <button
                        className="btn-modern btn-success-modern"
                        onClick={() => handleAcceptQueue(entry.chargerId)}
                        disabled={isActioning}
                      >
                        <Play size={16} />
                        {t('session.queue.acceptNow')}
                      </button>
                      <button
                        className="btn-modern btn-warning-modern"
                        onClick={() => handlePostponeQueue(entry.chargerId)}
                        disabled={isActioning}
                      >
                        <AlertCircle size={16} />
                        {t('session.queue.postpone')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Waiting Queue Entries */}
          {queueEntries.filter(e => !e.canAccept).length > 0 && (
            <div className="queue-entries-group">
              <h4 className="queue-group-title waiting-title">
                <Clock size={16} style={{ color: 'var(--color-warning)' }} />
                Waiting in Queue
              </h4>
              <div className="queue-entries-grid">
                {queueEntries.filter(e => !e.canAccept).map((entry) => (
                  <div key={entry.chargerId} className="modern-queue-card waiting">
                    <div className="queue-card-header">
                      <div className="queue-icon waiting">
                        <Clock size={20} style={{ color: 'var(--color-warning)' }} />
                      </div>
                      <div className="queue-basic-info">
                        <h4 className="queue-charger-name">
                          {entry.chargerName} - {t('home.charger.slot', { number: entry.chargerSlotNumber })}
                        </h4>
                        <p className="queue-position waiting">
                          Position #{entry.position} - Waiting
                        </p>
                      </div>
                      <div className="queue-status-badge waiting">
                        <Clock size={16} />
                        Waiting
                      </div>
                    </div>

                    <div className="queue-details-grid">
                      <div className="detail-item-modern">
                        <span className="detail-label">Joined</span>
                        <span className="detail-value">{new Date(entry.joinedAt).toLocaleString()}</span>
                      </div>
                      {entry.estimatedStartTime && (
                        <div className="detail-item-modern">
                          <span className="detail-label">Est. Available</span>
                          <span className="detail-value">{new Date(entry.estimatedStartTime).toLocaleTimeString()}</span>
                        </div>
                      )}
                    </div>

                    <div className="queue-actions-modern">
                      <button
                        className="btn-modern btn-secondary-modern"
                        onClick={() => handleCancelQueueEntry(entry.chargerId)}
                        disabled={isActioning}
                      >
                        {t('home.charger.actions.leaveQueue')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}